import * as joint from "@joint/core";
import {AvoidLib, ConnRef} from "libavoid-js";
import {Polygon, Router} from "libavoid-js";
import {g, dia } from "@joint/core";
import {AvoidRouter, VerticePoint} from "@app/netscope/netscope-common-flowview/misc/AvoidRouter";

export class RoutingOptions {
  maximumLoops?: number;
  routerAlgorithm: string;
}

export class VerticePointsWithCellsVersion {
  vertices : VerticePoint[] = [];
  isParent: boolean = false;
  srcCellVersion: number;
  srcCellId: string;
  dstCellVersion: number;
  dstCellId: string;
  srcParentCellVersion: number;
  srcParentCellId: string;
  dstParentCellVersion: number;
  dstParentCellId: string;

  constructor(source: joint.dia.Element, target: joint.dia.Element, isParent: boolean, vertices: VerticePoint[]) {
    this.isParent = isParent;

    this.vertices = vertices;

    if (source != undefined) {
      // @ts-ignore
      this.srcCellVersion = source.attributes.version;
      // @ts-ignore
      this.srcCellId = source.id.toString();
    }

    if (target != undefined) {
      // @ts-ignore
      this.dstCellVersion = target.attributes.version;
      // @ts-ignore
      this.dstCellId = target.id.toString();
    }

    if (! isParent) {

      // @ts-ignore
      let parentSource = source.attributes.parent != undefined ? source.getParentCell() : source;

      // @ts-ignore
      let parentTarget = target.attributes.parent != undefined ? target.getParentCell() : target;

      // @ts-ignore
      this.srcParentCellVersion = parentSource.attributes.version;
      // @ts-ignore
      this.srcParentCellId = parentSource.id.toString();
      // @ts-ignore
      this.dstParentCellVersion = parentTarget.attributes.version;
      // @ts-ignore
      this.dstParentCellId = parentTarget.id.toString();
    }
  }

  clone = () => {
    let verticePointsWithVersion = new VerticePointsWithCellsVersion(undefined, undefined, true, []);

    verticePointsWithVersion.vertices = this.vertices;
    verticePointsWithVersion.isParent = this.isParent;
    verticePointsWithVersion.srcCellVersion = this.srcCellVersion;
    verticePointsWithVersion.dstCellVersion = this.dstCellVersion;
    verticePointsWithVersion.srcCellId = this.srcCellId;
    verticePointsWithVersion.dstCellId = this.dstCellId;
    verticePointsWithVersion.srcParentCellVersion = this.srcParentCellVersion;
    verticePointsWithVersion.dstParentCellVersion = this.dstParentCellVersion;
    verticePointsWithVersion.srcParentCellId = this.srcParentCellId;
    verticePointsWithVersion.dstParentCellId = this.dstParentCellId;

    return verticePointsWithVersion;
  }

  shouldExpire = (source: joint.dia.Element, target: joint.dia.Element): boolean => {

    // @ts-ignore
    if (source.attributes.version > this.srcCellVersion || target.attributes.version > this.dstCellVersion) {
      // console.log(`has expired: ${key}`);
      return true;
    }

    if (! this.isParent) {

      // @ts-ignore
      let parentSource = source.attributes.parent != undefined ? source.getParentCell() : source;

      // @ts-ignore
      let parentTarget = target.attributes.parent != undefined ? target.getParentCell() : target;

      // @ts-ignore
      if (parentSource.attributes.version > this.srcParentCellVersion || parentTarget.attributes.version > this.dstParentCellVersion) {
        // console.log(`has expired: ${key}`);
        return true;
      }
    }

    // console.log(`not expired: ${key}`);
    return false;
  }
}

export class RouterWrapper {
  conRefMap: Map<string | number, ConnRef>;
  verticesMap: Map<string | number, VerticePoint[]>;

  constructor(private obstacles: Polygon[], private links: Map<string | number, [VerticePoint, VerticePoint]>) {
    this.conRefMap = new Map();
    this.verticesMap = new Map();
  }

  initRouter(router: Router, linkMap: Map<string | number, VerticePoint[]> = new Map()) {
    // Add obstacles
    for (let obstacle of this.obstacles) {
      const shapeRef = new AvoidLib.avoidLib.ShapeRef(router, obstacle);
    }

    // // Add obstacles
    // for (let [linkId, link] of linkMap.entries()) {
    //   const linkObstacle = this.buildPolyLine(link);
    //   const shapeRef = new AvoidLib.avoidLib.ShapeRef(router, linkObstacle);
    // }

    // Add links that need to be computed
    for (let [k, link] of this.links.entries()) {
      let src = link[0];
      let dst = link[1];

      const connSrc = new AvoidLib.avoidLib.ConnEnd(new AvoidLib.avoidLib.Point(src.x, src.y));
      const connEnd = new AvoidLib.avoidLib.ConnEnd(new AvoidLib.avoidLib.Point(dst.x, dst.y));
      const conn = new AvoidLib.avoidLib.ConnRef(router, connSrc, connEnd);

      this.conRefMap.set(k, conn);
    }
  }

  // private buildPolyLine = (pts: VerticePoint[]) => {
  //   // PolyLine is a Polygon of n points; set_ps mirrors the get_ps you already use.
  //   const poly = new AvoidLib.avoidLib.PolyLine(pts.length);
  //   for (let i = 0; i < pts.length; i++) {
  //     poly.set_ps(i, new AvoidLib.avoidLib.Point(pts[i].x, pts[i].y));
  //   }
  //   return poly;
  // };

  processAndComputeEverything = (linkMap: Map<string | number, VerticePoint[]> = new Map()): Map<string | number, VerticePoint[]> => {
    // Create router
    let router: Router = this.generateNewRouter();
    this.initRouter(router, linkMap);

    // Process transactions
    router.processTransaction();

    // Fetch vertices of all links
    for (let [k, link] of this.links.entries()) {
      let connRef = this.conRefMap.get(k);
      const route = connRef.displayRoute();
      let vertices: VerticePoint[] = [];

      for (let i = 0; i < route.size(); i++) {
        const {x, y} = route.get_ps(i);
        vertices.push({x: x, y: y});
      }

      this.verticesMap.set(k, vertices);
    }

    // Destroy and clean everything
    AvoidLib.avoidLib.destroy(router);

    return this.verticesMap;
  }

  private generateNewRouter = () => {
    const appGroupsRouter: Router = new AvoidLib.avoidLib.Router(AvoidLib.avoidLib.OrthogonalRouting);

    let shapeBufferDistance = 15;
    let idealNudgingDistance = 10;

    /* Prevent links to be too close to shapes that they are not connected to */
    // @ts-ignore
    appGroupsRouter.setRoutingParameter(AvoidLib.avoidLib.shapeBufferDistance, shapeBufferDistance);

    /* Allows more bends */
    // @ts-ignore
    appGroupsRouter.setRoutingParameter(AvoidLib.avoidLib.segmentPenalty, 10);
    // @ts-ignore
    appGroupsRouter.setRoutingParameter(AvoidLib.avoidLib.anglePenalty, 10);

    /* Disable expensive options for large graphs */
    // @ts-ignore
    appGroupsRouter.setRoutingOption(AvoidLib.avoidLib.performUnifyingNudgingPreprocessingStep, false); // use false to speed-up
    // @ts-ignore
    appGroupsRouter.setRoutingParameter(AvoidLib.avoidLib.idealNudgingDistance, idealNudgingDistance);

    // Penalise paths that share segments with existing routes
    // @ts-ignore
    appGroupsRouter.setRoutingOption(AvoidLib.avoidLib.nudgeOrthogonalSegmentsConnectedToShapes, true);

    // Nudge colinear segments that touch at their ends
    // @ts-ignore
    appGroupsRouter.setRoutingOption(AvoidLib.avoidLib.nudgeOrthogonalTouchingColinearSegments, true);

    // Penalise shared paths — forces libavoid to prefer distinct routes
    // @ts-ignore
    appGroupsRouter.setRoutingOption(AvoidLib.avoidLib.penaliseOrthogonalSharedPathsAtConnEnds, false);

    return appGroupsRouter;
  }
}

export class OptimizedAppsGroupsRouter extends AvoidRouter {

  linksPrecomputedVertices: Map<string, VerticePointsWithCellsVersion> = new Map();
  lastComputedVersion = -1;
  optimisationLevel = 0;
  // Obstacles further than this distance from a sublink's straight path are excluded from its routing.
  // Must stay large enough to leave room for the detours that libavoid takes around the kept obstacles.
  obstaclePruningMargin = 150;

  Avoid;

  constructor(graph: joint.dia.Graph, options: {
    shapeBufferDistance?: number,
    idealNudgingDistance?: number,
    paper?: joint.dia.Paper
  } = {}) {

    super(graph, options);
    this.Avoid = AvoidLib.getInstance();
  }

  public computeMargin(point, center, margin) {

    let marginX = 0;
    let marginY = 0;

    let dx = point.x - center.x;
    let dy = point.y - center.y;

    let distance = Math.sqrt(dx**2 + dy**2);

    if (distance > 0) {
      let ratio = (margin / distance)

      marginX = dx * ratio;
      marginY = dy * ratio;
    }

    return {
      x: point.x + marginX,
      y: point.y + marginY
    }
  }

  public interpolatePoints = (points: VerticePoint[], stepsPerSegment = 5): VerticePoint[] => {
    const result = [];

    for (let i = 0; i < points.length - 1; i++) {
      const a = new g.Point(points[i]);
      const b = new g.Point(points[i + 1]);

      for (let s = 0; s < stepsPerSegment; s++) {
        const t = s / stepsPerSegment;
        result.push(a.lerp(b, t));
      }
    }

    // add the final point
    result.push(new g.Point(points[points.length - 1]));
    return result; // g.Point[]
  }

  public verticesFromFirstPath = (link, srcElement: joint.dia.Element, tgtElement: joint.dia.Element, srcParentsBboxParams: joint.g.Rect, dstParentsBboxParams: joint.g.Rect, appGroupsFirstPathVertices: VerticePoint[], margin: number): VerticePoint[] => {
    let result: VerticePoint[] = [];

    let interpolatedPoints = this.interpolatePoints(appGroupsFirstPathVertices, 50);
    // let interpolatedPoints = appGroupsFirstPathVertices;

    let srcParentsBbox = srcParentsBboxParams.inflate(margin, margin);
    let dstParentsBbox = dstParentsBboxParams.inflate(margin, margin);

    let lastPointCloseToSrcParentBbox = undefined;
    let lastPointCloseToDstParentBbox = undefined;
    for (let point of interpolatedPoints) {

      if (srcParentsBbox.containsPoint(point)) {
        lastPointCloseToSrcParentBbox = point;
      }

      if (!srcParentsBbox.containsPoint(point) && ! dstParentsBbox.containsPoint(point)) {
        result.push(point);
      }

      if (dstParentsBbox.containsPoint(point) && lastPointCloseToDstParentBbox == undefined) {
        lastPointCloseToDstParentBbox = point;
      }
    }

    // Add src and dst point interpolated to last point inside the bboxes

    if (result.length > 0) {
      // SRC
      if (lastPointCloseToSrcParentBbox != undefined) {
        let firstPoint = result[0];
        const line = new g.Line(
          lastPointCloseToSrcParentBbox,
          firstPoint
        );
        let newFirstPoint = srcParentsBbox.intersectionWithLine(line)[0];
        let newFirstPointAsVertice: VerticePoint = {
          x: newFirstPoint.x,
          y: newFirstPoint.y
        }
        result = [newFirstPointAsVertice].concat(result);
      }

      // DST
      if (lastPointCloseToDstParentBbox != undefined) {
        let lastPoint = result[result.length - 1];
        const line = new g.Line(
          lastPointCloseToDstParentBbox,
          lastPoint
        );
        let newLastPoint = dstParentsBbox.intersectionWithLine(line)[0];
        let newLastPointAsVertice: VerticePoint = {
          x: newLastPoint.x,
          y: newLastPoint.y
        }
        result.push(newLastPointAsVertice);
      }

      return result;
    }

    if (margin > 10) {
      console.log(`Link '${link.attributes.namesKey}' may be too short for given margin (${margin}), its display may be degraded. I will try with a lower margin`);
      return this.verticesFromFirstPath(link, srcElement, tgtElement, srcParentsBboxParams, dstParentsBboxParams, appGroupsFirstPathVertices, 10);
    }
    console.log(`Link '${link.attributes.namesKey}' will have a degraded display.`);

    return [{
      x: (srcParentsBboxParams.center().x + dstParentsBboxParams.center().x) / 2,
      y: (srcParentsBboxParams.center().y + dstParentsBboxParams.center().y) / 2
    }];
    return appGroupsFirstPathVertices;
  }

  public parallelPolyline = (points: VerticePoint[], offset: number): g.Point[] => {
    if (points.length < 2) return points.map(p => new g.Point(p));

    // Step 1: compute the offset line for each segment
    const offsetLines: g.Line[] = [];

    for (let i = 0; i < points.length - 1; i++) {
      const a = new g.Point(points[i]);
      const b = new g.Point(points[i + 1]);

      // Direction vector of the segment
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const len = Math.sqrt(dx * dx + dy * dy);

      // Perpendicular unit vector (rotated 90° left)
      const nx = -dy / len;
      const ny =  dx / len;

      // Shift both endpoints by offset along the normal
      const offsetA = new g.Point(a.x + nx * offset, a.y + ny * offset);
      const offsetB = new g.Point(b.x + nx * offset, b.y + ny * offset);

      offsetLines.push(new g.Line(offsetA, offsetB));
    }

    // Step 2: build the parallel polyline points
    const result: g.Point[] = [];

    // First point: start of first offset segment
    result.push(offsetLines[0].start.clone());

    // Middle points: intersection of consecutive offset segments
    for (let i = 0; i < offsetLines.length - 1; i++) {
      const intersection = offsetLines[i].intersect(offsetLines[i + 1]);
      if (intersection) {
        result.push(intersection);
      } else {
        // Segments are parallel (collinear) — just use the endpoint
        result.push(offsetLines[i].end.clone());
      }
    }

    // Last point: end of last offset segment
    result.push(offsetLines[offsetLines.length - 1].end.clone());

    return result;
  }

  generateUuid(source: joint.dia.Element, target: joint.dia.Element, sort: boolean): string {
    let ids = [source.id.toString(), target.id.toString()];
    let sortedIds = sort ? ids.sort() : ids;

    return sortedIds.join("__");
  }

  generateNewRouter = () => {
    const appGroupsRouter: Router = new this.Avoid.Router(this.Avoid.OrthogonalRouting);

    let shapeBufferDistance = 15;
    let idealNudgingDistance = 10;

    /* Prevent links to be too close to shapes that they are not connected to */
    // @ts-ignore
    appGroupsRouter.setRoutingParameter(this.Avoid.shapeBufferDistance, shapeBufferDistance);

    /* Allows more bends */
    // @ts-ignore
    appGroupsRouter.setRoutingParameter(this.Avoid.segmentPenalty, 10);
    // @ts-ignore
    appGroupsRouter.setRoutingParameter(this.Avoid.anglePenalty, 10);

    /* Disable expensive options for large graphs */
    // @ts-ignore
    appGroupsRouter.setRoutingOption(this.Avoid.performUnifyingNudgingPreprocessingStep, false); // use false to speed-up
    // @ts-ignore
    appGroupsRouter.setRoutingParameter(this.Avoid.idealNudgingDistance, idealNudgingDistance);

    // Penalise paths that share segments with existing routes
    // @ts-ignore
    appGroupsRouter.setRoutingOption(this.Avoid.nudgeOrthogonalSegmentsConnectedToShapes, false);

    // Nudge colinear segments that touch at their ends
    // @ts-ignore
    appGroupsRouter.setRoutingOption(this.Avoid.nudgeOrthogonalTouchingColinearSegments, true);

    // Penalise shared paths — forces libavoid to prefer distinct routes
    // @ts-ignore
    appGroupsRouter.setRoutingOption(this.Avoid.penaliseOrthogonalSharedPathsAtConnEnds, true);

    return appGroupsRouter;
  }

  computeDistance = (pointA: VerticePoint, pointB: VerticePoint): number => {
    return Math.sqrt((pointA.x - pointB.x) ** 2 + (pointA.y -pointB.y) ** 2);
  }

  extractObstacles = (elements): Polygon[] => {
    let result: Polygon[] = elements
      .map((el) => {
        let elBBox = el.getBBox();
        let p1 = new this.Avoid.Point(elBBox.x, elBBox.y);
        let p2 = new this.Avoid.Point(elBBox.x + elBBox.width, elBBox.y + elBBox.height);
        const rectangle = new this.Avoid.Rectangle(p1, p2);
        return rectangle;
      });

    return result;
  }

  // Only keep the elements close to the straight path of a link: obstacles far from the path
  // cannot influence the resulting route, but they enlarge the visibility graph that libavoid searches.
  extractObstaclesNearSegments = (elements: dia.Element[], segments: [VerticePoint, VerticePoint][], margin: number): Polygon[] => {
    let corridors = segments.map((segment) => {
      let [a, b] = segment;
      let corridor = new g.Rect(
        Math.min(a.x, b.x),
        Math.min(a.y, b.y),
        Math.abs(a.x - b.x),
        Math.abs(a.y - b.y)
      );
      return corridor.inflate(margin, margin);
    });

    let nearbyElements = elements.filter((el) => {
      let elBBox = el.getBBox();
      return corridors.some((corridor) => corridor.intersect(elBBox) != null);
    });

    return this.extractObstacles(nearbyElements);
  }

  computeAllVertices = (paper: dia.Paper, routingOptions: RoutingOptions) => {
    // @ts-ignore
    this.lastComputedVersion = this.graph.version;
    // const router: Router = this.generateNewRouter();

    // console.log("Adding a shape.");

    let links: Map<string | number, [VerticePoint, VerticePoint]> = new Map();

    // Process Apps groups and compute inter Apps groups vertices
    let allAppsGroups = this.graph.getElements()
      // @ts-ignore
      .filter((el) => el.attributes.data.type == "parent");
    let allMembers = this.graph.getElements()
      // @ts-ignore
      .filter((el) => el.attributes.data.type == "member");
    let allNodes = this.graph.getElements()
      // @ts-ignore
      .filter((el) => el.attributes.data.type == "parent" || el.attributes.data.type == "member");

    let appGroupsIndex: Map<string, dia.Element> = new Map();
    allAppsGroups.forEach((el) => {
      appGroupsIndex.set(el.id.toString(), el);
    });

    let obstacles: Polygon[] = this.extractObstacles(allAppsGroups);

    let mapParentLinks = new Map<string, joint.dia.Link>();
    let parentLinksToLinks = new Map<string, joint.dia.Link[]>();

    for (let link of this.graph.getLinks()) {
      let parentLink = link.attributes["parentLink"];

      let sourceCell = link.getSourceCell();
      let targetCell = link.getTargetCell();

      if (parentLink != undefined) {
        // @ts-ignore
        let sourceParent = sourceCell.attributes.parent != undefined ? sourceCell.getParentCell() : sourceCell;
        // @ts-ignore
        let targetParent = targetCell.attributes.parent != undefined ? targetCell.getParentCell() : targetCell;

        // Link is a sublink (vm <-> vm)
        parentLink.sourceBBox = sourceParent.getBBox();
        parentLink.targetBBox = targetParent.getBBox();
        parentLink.sourceObject = sourceParent;
        parentLink.targetObject = targetParent;
        mapParentLinks.set(parentLink.id, parentLink);

        if (! parentLinksToLinks.has(parentLink.id)) {
          parentLinksToLinks.set(parentLink.id, []);
        }
        parentLinksToLinks.get(parentLink.id).push(link);
        // Link between apps-groups
        // continue;
      } else {
        // Link is a node link (app-group <-> app-group)
        // @ts-ignore
        link.sourceBBox = sourceCell.getBBox();
        // @ts-ignore
        link.targetBBox = targetCell.getBBox();
        // @ts-ignore
        link.sourceObject = sourceCell;
        // @ts-ignore
        link.targetObject = targetCell;

        mapParentLinks.set(link.id.toString(), link);
        parentLinksToLinks.set(link.id.toString(), []);
      }
    }

    // Compute all vertices of inter apps-groups links
    let linkMap: Map<string | number, VerticePoint[]> = new Map();
    for (let parentLink of mapParentLinks.values()) {
      // @ts-ignore
      let parentLinkKey = this.generateUuid(parentLink.attributes.source, parentLink.attributes.target, false);

      if (this.linksPrecomputedVertices.has(parentLinkKey)) {
        let existingVerticesAndVersions = this.linksPrecomputedVertices.get(parentLinkKey);
        // @ts-ignore
        if (! existingVerticesAndVersions.shouldExpire(parentLink.sourceObject, parentLink.targetObject)) {
          continue;
        }
      }

      // // @ts-ignore
      // let srcCenter = parentLink.sourceBBox.center();
      // // @ts-ignore
      // let dstCenter = parentLink.targetBBox.center();

      let noiseAmplitude = 10;
      // @ts-ignore
      let srcCenter = parentLink.sourceBBox.center();
      // @ts-ignore
      let dstCenter = parentLink.targetBBox.center();
      let noise = 2 * noiseAmplitude * Math.random();

      links.set(parentLink.id, [
        {x: srcCenter.x + noise, y: srcCenter.y + noise},
        {x: dstCenter.x + noise, y: dstCenter.y + noise}
      ]);

      // @ts-ignore
      if (parentLink.attributes.namesKey == "172.16.9.0/24__172.30.3.0/24") {
        console.log(`[
          {x: ${srcCenter.x}, y: ${srcCenter.y}},
          {x: ${dstCenter.x}, y: ${dstCenter.y}}
        ]`);
      }
    }

    for (let [linkId, link] of links.entries()) {
      // Compute positions
      // router.processTransaction();
      let singleLinkMap: Map<string | number, [VerticePoint, VerticePoint]> = new Map();
      singleLinkMap.set(linkId, link);

      let obstaclesForThisLink = [].concat(obstacles)

      if (linkId.toString().includes("vim.") || linkId.toString().includes("xo.") || linkId.toString().includes("nu.")) {
        // @ts-ignore
        let obstaclesNearSubLink = this.extractObstaclesNearSegments(allNodes, [link], this.obstaclePruningMargin);
        obstaclesForThisLink = obstaclesForThisLink.concat(obstaclesNearSubLink);
      }

      const newRouter = new RouterWrapper(obstaclesForThisLink, singleLinkMap);
      console.time("newRouter.processAndComputeEverything()");
      let linkMapForThisLink = newRouter.processAndComputeEverything(linkMap);
      console.timeEnd("newRouter.processAndComputeEverything()");

      for (let [k, v] of linkMapForThisLink.entries()) {
        linkMap.set(k, v);
      }
    }

    // // Compute positions
    // // router.processTransaction();
    // const newRouter = new RouterWrapper(obstacles, links);
    // console.time("newRouter.processAndComputeEverything()");
    // let linkMap = newRouter.processAndComputeEverything();
    // console.timeEnd("newRouter.processAndComputeEverything()");

    // Get all the apps-groups routes
    for (let parentLink of mapParentLinks.values()) {
      // @ts-ignore
      let parentLinkKey = this.generateUuid(parentLink.attributes.source, parentLink.attributes.target, false);

      // // @ts-ignore
      // if (parentLink.attributes.parents != undefined) {
      //   // @ts-ignore
      //   parentLinkKey = this.generateUuid({id: parentLink.attributes.parents.source.uuid}, {id: parentLink.attributes.parents.target.uuid}, false);
      // }

      if (this.linksPrecomputedVertices.has(parentLinkKey)) {
        let existingVerticesAndVersions = this.linksPrecomputedVertices.get(parentLinkKey);
        // @ts-ignore
        if (! existingVerticesAndVersions.shouldExpire(parentLink.sourceObject, parentLink.targetObject)) {
          continue;
        }
      }

      let vertices: VerticePoint[] = [];
      // @ts-ignore
      if (! parentLink.attributes.internal) {
        vertices = linkMap.get(parentLink.id);
        // @ts-ignore
        let correctedVertices = this.verticesFromFirstPath(parentLink, null, null, parentLink.sourceBBox, parentLink.targetBBox, vertices, 100);
        vertices = correctedVertices;
      }

      // @ts-ignore
      let invertedAppGroupKey = parentLinkKey.split("__").reverse().join("__");

      // @ts-ignore
      let versionedVertices = new VerticePointsWithCellsVersion(parentLink.sourceObject, parentLink.targetObject, true, vertices);

      this.linksPrecomputedVertices.set(parentLinkKey, versionedVertices);

      // @ts-ignore
      let versionedVerticesInverted = new VerticePointsWithCellsVersion(parentLink.targetObject, parentLink.sourceObject, true, vertices.toReversed());

      this.linksPrecomputedVertices.set(invertedAppGroupKey, versionedVerticesInverted);
    }

    // Now Process the links that are inside appsGroups
    for (let parentLink of mapParentLinks.values()) {
      // TODO: optimize for hybrid here !

      let subLinks = parentLinksToLinks.get(parentLink.id.toString());

      let processThisLink = false;
      for (let subLink of subLinks) {
        let source = subLink.getSourceElement();
        let target = subLink.getTargetElement();

        if (this.linksPrecomputedVertices.has(subLink.id.toString())) {
          let existingVerticesAndVersions = this.linksPrecomputedVertices.get(subLink.id.toString());
          // @ts-ignore
          if (existingVerticesAndVersions.shouldExpire(source, target)) {
            processThisLink = true;
          }
        } else {
          processThisLink = true;
        }
      }

      if (! processThisLink) {
       continue;
      }

      // Process Apps groups and compute inter Apps groups vertices
      // @ts-ignore
      let parentSourceId = parentLink.attributes.source.id;
      // @ts-ignore
      let parentTargetId = parentLink.attributes.target.id;

      let allAppsGroupsMembers = this.graph.getElements()
        // @ts-ignore
        .filter((el) => el.attributes.data.type != "parent")
        .filter((el) => {
          // @ts-ignore
          return el.attributes.parent.id == parentSourceId || el.attributes.parent.id == parentTargetId;
        });

      let mapSubLinks = new Map<string, joint.dia.Link>();
      for (let subLink of subLinks) {
        // @ts-ignore
        subLink.sourceBBox = subLink.getSourceCell().getBBox();
        // @ts-ignore
        subLink.targetBBox = subLink.getTargetCell().getBBox();

        // @ts-ignore
        mapSubLinks.set(subLink.id, subLink);
      }

      // Compute all vertices of inter apps-groups links
      // @ts-ignore
      let isInternalLink: boolean = parentLink.attributes.internal;

      let mapSubLinkOffset = new Map<string, number>();
      let mapSubLinkInverted = new Map<string, boolean>();

      let interLinksCounter = 0;

      let mapLinkVertices: Map<string | number, VerticePoint[]> = new Map();

      let allSubLinksMap: Map<string | number, [VerticePoint, VerticePoint]> = new Map();

      for (let subLink of subLinks) {

        let anchors = this.decideAnchors(subLink.getSourceElement(), subLink.getTargetElement());

        let srcCenter = subLink.getSourceElement().getCenter();
        let dstCenter = subLink.getTargetElement().getCenter();

        let srcAnchorWithMargin = this.computeMargin(anchors.src, srcCenter, 30);
        let dstAnchorWithMargin = this.computeMargin(anchors.dst, dstCenter, 30);

        if (isInternalLink) {
          let subLinkMap: Map<string | number, [VerticePoint, VerticePoint]> = new Map();
          let segment: [VerticePoint, VerticePoint] = [{x: srcAnchorWithMargin.x, y: srcAnchorWithMargin.y}, {x: dstAnchorWithMargin.x, y: dstAnchorWithMargin.y}];
          subLinkMap.set(`${subLink.id}`, segment);

          mapSubLinkOffset.set(subLink.id.toString(), interLinksCounter);

          let obstaclesNearSubLink = this.extractObstaclesNearSegments(allAppsGroupsMembers, [segment], this.obstaclePruningMargin);
          const newSubLinkRouter = new RouterWrapper(obstaclesNearSubLink, subLinkMap);
          console.time("let linkMap = newSubLinkRouter.processAndComputeEverything();")
          let linkMap = newSubLinkRouter.processAndComputeEverything();
          console.timeEnd("let linkMap = newSubLinkRouter.processAndComputeEverything();")

          for (let [k, v] of linkMap.entries()) {
            mapLinkVertices.set(k, v);
          }
        } else {
          // @ts-ignore
          let parentLinkKey = this.generateUuid(parentLink.attributes.source, parentLink.attributes.target, false);

          let versionedVertices = this.linksPrecomputedVertices.get(parentLinkKey);

          let parentVertices = versionedVertices.vertices;

          let subLinkSource = subLink.getSourceElement();
          // @ts-ignore
          let subLinkSourceParentElement = subLinkSource.attributes.parent != undefined ? subLinkSource.getParentCell() : subLinkSource;

          let inverseParentLink = false;
          if (subLinkSourceParentElement.id != versionedVertices.srcCellId) {
            inverseParentLink = true;
          }
          mapSubLinkInverted.set(subLink.id.toString(), inverseParentLink);

          let parentLinkVertices2 = parentVertices;

          parentLinkVertices2 = this.parallelPolyline(parentLinkVertices2, 1 * interLinksCounter);

          let commonSegmentFirstPoint = parentLinkVertices2[0];
          let commonSegmentLastPoint = parentLinkVertices2[parentLinkVertices2.length - 1];

          let firstSegment: [VerticePoint, VerticePoint];
          let secondSegment: [VerticePoint, VerticePoint];

          if (inverseParentLink) {
            firstSegment = [{x: srcAnchorWithMargin.x, y: srcAnchorWithMargin.y}, {x: commonSegmentLastPoint.x, y: commonSegmentLastPoint.y}];
            secondSegment = [{x: commonSegmentFirstPoint.x, y: commonSegmentFirstPoint.y}, {x: dstAnchorWithMargin.x, y: dstAnchorWithMargin.y}];
          } else {
            firstSegment = [{x: srcAnchorWithMargin.x, y: srcAnchorWithMargin.y}, {x: commonSegmentFirstPoint.x, y: commonSegmentFirstPoint.y}];
            secondSegment = [{x: commonSegmentLastPoint.x, y: commonSegmentLastPoint.y}, {x: dstAnchorWithMargin.x, y: dstAnchorWithMargin.y}];
          }

          let subLinkMap: Map<string | number, [VerticePoint, VerticePoint]> = new Map();
          subLinkMap.set(`${subLink.id}__s1`, firstSegment);
          subLinkMap.set(`${subLink.id}__s2`, secondSegment);

          allSubLinksMap.set(`${subLink.id}__s1`, firstSegment);
          allSubLinksMap.set(`${subLink.id}__s2`, secondSegment);

          mapSubLinkOffset.set(subLink.id.toString(), interLinksCounter);

          let obstaclesNearSubLink = this.extractObstaclesNearSegments(allAppsGroupsMembers, [firstSegment, secondSegment], this.obstaclePruningMargin);
          const newSubLinkRouter = new RouterWrapper(obstaclesNearSubLink, subLinkMap);
          console.time("let linkMap = newSubLinkRouter.processAndComputeEverything();");
          let linkMap = newSubLinkRouter.processAndComputeEverything();
          console.timeEnd("let linkMap = newSubLinkRouter.processAndComputeEverything();");

          for (let [k, v] of linkMap.entries()) {
            mapLinkVertices.set(k, v);
          }

          // interLinksCounter += 1;
        }
      }

      // Get all the apps-groups routes
      for (let subLink of subLinks) {

        let source = subLink.getSourceElement();
        let target = subLink.getTargetElement();

        if (this.linksPrecomputedVertices.has(subLink.id.toString())) {
          let existingVerticesAndVersions = this.linksPrecomputedVertices.get(subLink.id.toString());
          // @ts-ignore
          if (! existingVerticesAndVersions.shouldExpire(source, target)) {
            continue;
          }
        }

        if (isInternalLink) {
          let vertices: VerticePoint[] = mapLinkVertices.get(`${subLink.id}`);
          let versionedVertices = new VerticePointsWithCellsVersion(source, target, false, vertices);
          this.linksPrecomputedVertices.set(subLink.id.toString(), versionedVertices);
        } else {
          let verticesS1: VerticePoint[] = [];
          let verticesS2: VerticePoint[] = [];
          let verticesS3: VerticePoint[] = [];
          let vertices: VerticePoint[] = [];


          if (routingOptions.routerAlgorithm !== "hybrid") {
            verticesS1 = mapLinkVertices.get(`${subLink.id}__s1`);
          }

          // Segment 2
          // @ts-ignore
          let parentLinkKey = this.generateUuid(subLink.attributes.parentLink.attributes.source, subLink.attributes.parentLink.attributes.target, false);

          let parentLinkVerticesWithVersion = this.linksPrecomputedVertices.get(parentLinkKey);
          let parentLinkVertices2 = parentLinkVerticesWithVersion.vertices;

          let inverseParentLink = mapSubLinkInverted.get(subLink.id.toString());
          if (inverseParentLink) {
            // @ts-ignore
            parentLinkVertices2 = parentLinkVertices2.toReversed();
          }

          let offset = mapSubLinkOffset.get(subLink.id.toString());
          let offsetSize = 1;
          parentLinkVertices2 = this.parallelPolyline(parentLinkVertices2, offsetSize * offset);

          verticesS2 = parentLinkVertices2;

          // Segment 3
          if (routingOptions.routerAlgorithm !== "hybrid") {
            verticesS3 = mapLinkVertices.get(`${subLink.id}__s2`);
          }

          vertices = verticesS1.concat(verticesS2).concat(verticesS3);

          let versionedVertices = new VerticePointsWithCellsVersion(source, target, false, vertices);

          // Fix with noise
          let noiseAmplitude = 20;
          let noise = 2 * noiseAmplitude * Math.random();

          for (let vertice of vertices) {
            vertice.x = vertice.x + noise;
            vertice.y = vertice.y + noise;
          }

          this.linksPrecomputedVertices.set(subLink.id.toString(), versionedVertices);
        }
      }
    }
  }

  public getCollisionPoint = (element: dia.Element, pointA: g.Point | { x: number; y: number }, pointB: g.Point | { x: number; y: number }): g.Point | null  => {
    const bbox = element.getBBox();
    const line = new g.Line(pointA, pointB);

    const intersections = bbox.intersectionWithLine(line);

    if (!intersections || intersections.length === 0) {
      return null;
    }

    // If two intersection points, return the one closest to pointA
    if (intersections.length === 2) {
      const a = new g.Point(pointA);
      return intersections[0].distance(a) < intersections[1].distance(a) ? intersections[0] : intersections[1];
    }

    return intersections[0];
  }

  public decideAnchors = (srcElement: dia.Element, dstElement: dia.Element) => {
    let result = {
      src: srcElement.getBBox().bottomMiddle(),
      dst: dstElement.getBBox().bottomMiddle(),
    }

    const point = this.getCollisionPoint(srcElement, srcElement.getCenter(), dstElement.getCenter());
    const point2 = this.getCollisionPoint(dstElement, srcElement.getCenter(), dstElement.getCenter());

    if (point != null) {
      result.src = point;
    }
    if (point2 != null) {
      result.dst = point2;
    }

    return result;
  }

  override clean = (element: joint.dia.Element) => {
    // console.log(`clean element: ${element}`);
    // let isParent = element.has("children");
  }

  public routingFunction = (vertices, options: RoutingOptions, linkView) => {

    // @ts-ignore
    let linkId = linkView.model.id;

    // @ts-ignore
    if (linkView.model.attributes.parentLink == undefined) {
      linkId = this.generateUuid(linkView.model.getSourceElement(), linkView.model.getTargetElement(), false);
    }

    if (this.linksPrecomputedVertices.has(linkId)) {
      let existingVerticesAndVersion = this.linksPrecomputedVertices.get(linkId);
      // @ts-ignore
      let source =  linkView.model.getSourceElement();
      // @ts-ignore
      let target =  linkView.model.getTargetElement();
      if (! existingVerticesAndVersion.shouldExpire(source, target)) {
        return existingVerticesAndVersion.vertices;
      }
    }

    // @ts-ignore
    let graphVersion = this.graph.version;
    if (graphVersion > this.lastComputedVersion) {
      console.time("computeAllVertices1");
      this.computeAllVertices(linkView.paper, options);
      console.timeEnd("computeAllVertices1");
    }

    let result = [];

    if (! this.linksPrecomputedVertices.has(linkId)) {
      console.log(`could not find link '${linkId}'`);
      console.log("debug");
      this.computeAllVertices(linkView.paper, options);
      console.log("debug2");
    }

    if (this.linksPrecomputedVertices.has(linkId)) {
      result = this.linksPrecomputedVertices.get(linkId).vertices;
    }

    return result;
  }
}
